import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Skull, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { formatPrice } from '../lib/format';
import { getProductCardImage } from '../lib/images';

const CartPage = () => {
  const { items, subtotal, count, removeItem, updateQuantity } = useCart();
  const navigate = useNavigate();

  return (
    <div className="min-h-[80vh] bg-gray-100 p-4 md:p-10">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-5xl md:text-7xl font-black tracking-tighter mb-8">YOUR CART</h1>

        {items.length === 0 ? (
          <div className="bg-white border-4 border-black p-16 text-center shadow-[8px_8px_0px_black]">
            <Skull size={64} className="mx-auto mb-4 opacity-40" />
            <h2 className="text-3xl font-black mb-2">CART IS EMPTY</h2>
            <p className="text-gray-600 mb-6">Feed your soul. Pick something up.</p>
            <Link
              to="/anime"
              className="inline-block bg-yellow-400 text-black px-8 py-4 font-black uppercase border-4 border-black shadow-[4px_4px_0px_black] hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all"
            >
              SHOP OTAKU →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              {items.map((item) => {
                const img = getProductCardImage(item);
                return (
                  <div
                    key={`${item._id || item.slug}-${item.size}`}
                    className="bg-white border-4 border-black p-4 flex gap-4 shadow-[4px_4px_0px_black]"
                  >
                    {img ? (
                      <Link to={`/${item.category}/${item.slug || item._id}`} className="block">
                        <img src={img} alt={item.name} className="w-28 h-28 object-cover border-2 border-black flex-shrink-0" />
                      </Link>
                    ) : (
                      <div className="w-28 h-28 bg-gray-200 border-2 border-black flex items-center justify-center text-5xl font-black">
                        {item.name?.charAt(0)}
                      </div>
                    )}
                    <div className="flex-grow min-w-0">
                      <Link
                        to={`/${item.category}/${item.slug || item._id}`}
                        className="font-black text-xl uppercase block hover:underline truncate"
                      >
                        {item.name}
                      </Link>
                      <div className="text-xs font-mono uppercase opacity-60 mb-2">
                        {item.category} · {item.type || 'Drop'}
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-bold text-xs bg-black text-white px-2 py-1 uppercase">
                          Size {item.size}
                        </span>
                        <div className="inline-flex items-center border-2 border-black">
                          <button
                            onClick={() => updateQuantity(item._id || item.id, item.size, (item.quantity || 1) - 1)}
                            className="px-2 py-1 hover:bg-yellow-400"
                            aria-label="Decrease"
                          >
                            <Minus size={14} />
                          </button>
                          <span className="px-3 font-bold">{item.quantity || 1}</span>
                          <button
                            onClick={() => updateQuantity(item._id || item.id, item.size, (item.quantity || 1) + 1)}
                            className="px-2 py-1 hover:bg-yellow-400"
                            aria-label="Increase"
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                        <button
                          onClick={() => removeItem(item._id || item.id, item.size)}
                          className="text-red-600 font-bold hover:bg-red-100 px-2 py-1 text-xs ml-auto"
                        >
                          REMOVE
                        </button>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-black text-2xl">
                        {formatPrice((item.salePrice ?? item.price) * (item.quantity || 1))}
                      </div>
                      {(item.quantity || 1) > 1 && (
                        <div className="text-xs text-gray-500 font-mono">
                          {formatPrice(item.salePrice ?? item.price)} × {item.quantity}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <aside className="lg:col-span-1">
              <div className="bg-white border-4 border-black p-6 shadow-[8px_8px_0px_black] sticky top-24">
                <h2 className="text-2xl font-black mb-4">SUMMARY</h2>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between">
                    <span>Items</span>
                    <span className="font-bold">{count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span className="font-bold">{formatPrice(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Shipping</span>
                    <span>Calculated at checkout</span>
                  </div>
                </div>
                <div className="border-t-2 border-black pt-4 mb-6 flex justify-between text-2xl font-black">
                  <span>TOTAL</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                <button
                  onClick={() => navigate('/checkout')}
                  className="w-full bg-yellow-400 text-black py-4 font-black text-lg border-4 border-black shadow-[4px_4px_0px_black] hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all flex items-center justify-center gap-2"
                >
                  CHECKOUT <ArrowRight />
                </button>
                <Link
                  to="/anime"
                  className="block mt-3 text-center text-sm font-bold underline hover:no-underline"
                >
                  ← Continue shopping
                </Link>
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;
